package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

@Serializable
data class DailyRecord(
    val id: String,
    val rosterId: String,
    val workplaceId: String,
    val date: String,
    val revenue: DailyRevenue,
    val laborStats: DailyLaborStats,
    val costs: DailyCosts,
    val efficiency: DailyEfficiency,
    val isLocked: Boolean = false
) {
    /*
     * The day's result ("Wynik", SH-46, as in the owner's spreadsheet). With a commission the owner
     * runs the place for someone else and earns the commission on its net sales; without one the
     * owner keeps the net sales. Result = that sales income + fixed revenue + revenue items −
     * (employer labour cost + other costs).
     */

    /** What the owner earns on the day as planned: the sales income on the target plus the fixed revenue and revenue items. */
    val incomePlan: Double get() = revenue.salesIncomePlan + revenue.fixedRevenue + revenue.otherRevenue

    /** What the owner earned on the day: the sales income on the takings plus the fixed revenue and revenue items. */
    val incomeReal: Double get() = revenue.salesIncomeReal + revenue.fixedRevenue + revenue.otherRevenue

    val resultPlan: Double
        get() = incomePlan - (costs.plannedLabour + costs.otherCosts)

    val resultReal: Double
        get() = incomeReal - (costs.actualByContractType.values.sum() + costs.otherCosts)

    /** SH-195: the planned result as a share of the planned income, in percent; null without income. */
    val resultPercentPlan: Double? get() = DayResults.percent(resultPlan, incomePlan)

    /** SH-195: the actual result as a share of the actual income; null until the takings are in (or without income). */
    val resultPercentReal: Double? get() = if (revenue.realNetto == null) null else DayResults.percent(resultReal, incomeReal)

    /*
     * SH-95: labour % of income ([LabourShare]): the employer labour cost against the commission
     * on the sales plus the fixed revenue (not the ledger's revenue items); without a commission,
     * against the net sales as before. The stored [LaborSummary.laborPercentage] stays a share of
     * net sales (older app versions and documents read it so).
     */

    /** What [labourPercentPlan] / [labourPercentReal] are measured against. */
    val labourBase: LabourBase get() = LabourShare.baseOf(revenue.commissionPercent, revenue.fixedRevenue)

    /** The day's planned income (or the target sales without a commission). */
    val labourBasePlan: Double
        get() = LabourShare.baseAmount(revenue.planNetto, revenue.commissionPercent, revenue.fixedRevenue)

    /** The day's actual income (or takings); null until the takings are in. */
    val labourBaseReal: Double?
        get() = revenue.realNetto?.let { LabourShare.baseAmount(it, revenue.commissionPercent, revenue.fixedRevenue) }

    /** Planned labour cost ÷ [labourBasePlan] × 100; null without a base. */
    val labourPercentPlan: Double? get() = LabourShare.percent(laborStats.planned.totalCost, labourBasePlan)

    /** Actual labour cost ÷ [labourBaseReal] × 100; null until the takings are in (or without a base). */
    val labourPercentReal: Double? get() = labourBaseReal?.let { LabourShare.percent(laborStats.actual.totalCost, it) }

    /** Planned labour cost as a share of the target net sales (shown beside the income share). */
    val labourPercentOfSalesPlan: Double? get() = LabourShare.percent(laborStats.planned.totalCost, revenue.planNetto)

    /** Actual labour cost as a share of the net takings; null until they are in. */
    val labourPercentOfSalesReal: Double? get() = revenue.realNetto?.let { LabourShare.percent(laborStats.actual.totalCost, it) }

    /** SH-191: planned SPMH ([Spmh.plan]): the net sales target ÷ the planned hours; null without either. */
    val spmhPlan: Double? get() = Spmh.plan(revenue.planNetto, costs.plannedHours)

    /**
     * SH-191: actual SPMH ([Spmh.actual]): the net takings ÷ the hours worked (the planned hours
     * while none are counted); null until the takings are in or without hours.
     */
    val spmhActual: Double? get() = Spmh.actual(revenue.realNetto, costs.actualHours, costs.assignedPlannedHours)
}

@Serializable
data class DailyLaborStats(
    val planned: LaborSummary,
    val actual: LaborSummary
)

@Serializable
data class LaborSummary(
    val totalCost: Double,
    val totalHours: Double,
    /** Labour cost ÷ net sales × 100, as stored (SH-95: the screens show [DailyRecord.labourPercentPlan] / Real). */
    val laborPercentage: Double
)

/**
 * A day's sales and income. Everything is NET (SH-70): the plan (the weekday sales target or a
 * per-day override), the takings and the fixed revenue; [commissionPercent] (a fraction,
 * 0.25 = 25%) is the commission the owner EARNS on the net sales (SH-46: the owner's income, not
 * a cost; 100% is the whole net sales). [realBrutto] / [previousYearBrutto] are only kept from older records.
 */
@Serializable
data class DailyRevenue(
    val planNetto: Double,
    val realBrutto: Double? = null,
    val realNetto: Double? = null,
    val previousYearBrutto: Double? = null,
    val previousYearNetto: Double? = null,
    val fixedRevenue: Double = 0.0,
    val commissionPercent: Double = 0.0,
    val comments: String? = null,
    /** SH-87: the month's revenue items (recurring or one-off) dated on this day. */
    val otherRevenue: Double = 0.0,
    /**
     * SH-123: where the takings came from (null on records from before: typed by hand). An
     * import never replaces takings typed by hand ([isEnteredByHand]).
     */
    val source: RevenueSource? = null,
    /** SH-123: when an import wrote the takings (ISO-8601 instant); null for takings typed by hand. */
    val importedAt: String? = null
) {
    /** Takings typed by hand (also those stored before SH-123, which carry no source): imports keep them. */
    val isEnteredByHand: Boolean
        get() = realNetto != null && (source == null || source == RevenueSource.MANUAL)

    val yoyPercentage: Double?
        get() {
            val prev = previousYearNetto ?: return null
            val current = realNetto ?: return null
            if (prev == 0.0) return null
            return ((current - prev) / prev) * 100.0
        }

    val commissionPlan: Double
        get() = planNetto * commissionPercent

    val commissionReal: Double
        get() = (realNetto ?: 0.0) * commissionPercent

    /** What the owner keeps of the target: the commission, or the whole net sales without one ([LabourShare.salesIncome]). */
    val salesIncomePlan: Double
        get() = LabourShare.salesIncome(planNetto, commissionPercent)

    /** What the owner keeps of the takings (0 until they are in). */
    val salesIncomeReal: Double
        get() = LabourShare.salesIncome(realNetto ?: 0.0, commissionPercent)
}

@Serializable
data class DailyCosts(
    val plannedHours: Double,
    val actualHours: Double,
    val plannedByContractType: Map<SystemContractType, Double>,
    val actualByContractType: Map<SystemContractType, Double>,
    val otherCosts: Double = 0.0,
    /**
     * Paid shifts of the day nobody could price (no cost snapshot and no pay rates readable or set):
     * not in the costs or hours above. Computed, not stored.
     */
    @kotlinx.serialization.Transient
    val unpricedShifts: Int = 0,
    @kotlinx.serialization.Transient
    val unpricedHours: Double = 0.0,
    /** Whose those shifts are (to set their pay rates). */
    @kotlinx.serialization.Transient
    val unpricedUserIds: List<String> = emptyList(),
    /**
     * SH-152: the planned and actual labour above per person (by user id), and the unpriced shifts
     * per person: what "how was this calculated?" lists. Computed with the totals, never stored, so
     * empty on a record read back from Firestore.
     */
    @kotlinx.serialization.Transient
    val plannedByPerson: Map<String, PersonLabour> = emptyMap(),
    @kotlinx.serialization.Transient
    val actualByPerson: Map<String, PersonLabour> = emptyMap(),
    @kotlinx.serialization.Transient
    val unpricedByPerson: Map<String, PersonLabour> = emptyMap(),
    /**
     * SH-206: the shifts of people who do not count in the labour figures, per person (their
     * planned hours): listed by "how was this calculated?", in none of the figures above.
     */
    @kotlinx.serialization.Transient
    val excludedByPerson: Map<String, PersonLabour> = emptyMap(),
    /**
     * SH-193: the day's unassigned shifts (demand nobody fills yet) in the plan: their hours are
     * in [plannedHours]; [openCost] (their hours × [openCostPerHour], an estimate) is in the planned
     * labour cost ([plannedLabour]), never in the actual. [openCostPerHour] null: not priced.
     */
    val openShifts: Int = 0,
    val openHours: Double = 0.0,
    val openCost: Double = 0.0,
    val openCostPerHour: Double? = null,
    /** SH-193: where [openCostPerHour] came from (OpenShiftRateSource name); null on records from before. */
    val openRateSource: String? = null,
    /**
     * SH-195: the ledger items making up [otherCosts] on this day (a recurring item's daily share,
     * a one-off item's amount or its part of its period), for the result's breakdown. Computed
     * with the day, never stored: empty on a record read back from Firestore.
     */
    @kotlinx.serialization.Transient
    val otherCostItems: List<LedgerDayItem> = emptyList(),
    /** SH-195: the same for the day's revenue items ([DailyRevenue.otherRevenue]). */
    @kotlinx.serialization.Transient
    val otherRevenueItems: List<LedgerDayItem> = emptyList()
) {
    /** SH-193: the planned employer labour cost: the assigned shifts by contract type plus the unassigned ones' estimate. */
    val plannedLabour: Double get() = plannedByContractType.values.sum() + openCost

    /** SH-193: the planned hours of the assigned shifts only (the actual SPMH falls back on these, never on unassigned ones). */
    val assignedPlannedHours: Double get() = (plannedHours - openHours).coerceAtLeast(0.0)
}

/** SH-152: one person's part of a day's (or a month's) labour: employer cost, hours and shifts. */
@Serializable
data class PersonLabour(val cost: Double = 0.0, val hours: Double = 0.0, val shifts: Int = 0) {
    operator fun plus(other: PersonLabour) = PersonLabour(cost + other.cost, hours + other.hours, shifts + other.shifts)
}

/** SH-152: [other]'s people added to these, person by person. */
fun Map<String, PersonLabour>.plusPeople(other: Map<String, PersonLabour>): Map<String, PersonLabour> {
    if (other.isEmpty()) return this
    val sum = toMutableMap()
    other.forEach { (id, labour) -> sum[id] = (sum[id] ?: PersonLabour()) + labour }
    return sum
}

@Serializable
data class DailyEfficiency(
    /**
     * Sales per man-hour as stored (net takings ÷ hours worked), kept for older readers. The screens
     * show [DailyRecord.spmhActual], worked out from the record's own takings and hours (SH-191).
     */
    val spmhReal: Double? = null
)

/** SH-123: where a day's takings came from. */
@Serializable
enum class RevenueSource {
    /** Typed in the app (Daily Records, the dashboard prompt). Imports never overwrite these. */
    MANUAL,
    /** A CSV/XLSX file uploaded in the app (Sales import). */
    CSV,
    /** Read from the till's API by the scheduled import (functions/posImport.js). */
    POS;

    val isImport: Boolean get() = this != MANUAL

    companion object {
        fun fromId(id: String?): RevenueSource? = entries.firstOrNull { it.name == id }
    }
}

/** SH-195: one ledger item's part of a day: its name, the amount on the day, and whether it recurs monthly (spread over the month). */
@Serializable
data class LedgerDayItem(val name: String, val amount: Double, val recurring: Boolean)

/*
 * SH-195: what the owner earns on a day (the daily result, "Wynik dnia") and over any run of days.
 * The same rule as the monthly result (MonthlyResult), day by day:
 *
 *   income = the commission on the net sales (the whole net sales without a commission)
 *            + the day's share of the fixed monthly revenue + the day's revenue items
 *   costs  = the employer labour cost (planned with the unassigned shifts' estimate, SH-193;
 *            actual as worked) + the day's share of the recurring costs + its one-off costs
 *   result = income − costs, and as a share of the income
 *
 * Recurring ledger items and the fixed revenue are spread over the month's days in whole cents
 * with the remainder on the last day, so a month's days add up to its monthly result. A period's
 * figures are sums of its days, never averages, and nothing is rounded before display.
 *
 * The spread of the recurring ledger items is the daily result's alone ([resultDay]): the stored
 * daily records (costs.otherCosts, revenue.otherRevenue) keep each item whole on its date, as old
 * app versions and the Cloud Functions read and write them.
 */
object DayResults {
    /**
     * [record] as its daily result counts it: each of the month's recurring [ledger] items
     * ([FixedCost.spreadsOverMonth]) its share of the day ([FixedCost.resultShareOn]) instead of
     * all of it on its date, item by item for the breakdown; the rest as the record holds it.
     * Without a ledger (not loaded) the record as it is.
     */
    fun resultDay(record: DailyRecord, ledger: List<FixedCost>): DailyRecord {
        if (ledger.isEmpty()) return record
        val day = record.date
        val (revenueItems, costItems) = ledger.partition { it.isRevenue }
        fun moved(items: List<FixedCost>) = items.sumOf { it.resultShareOn(day) - it.amountOn(day) }
        fun itemsOn(items: List<FixedCost>) = items.mapNotNull { item ->
            item.resultShareOn(day).takeIf { it != 0.0 }?.let { LedgerDayItem(item.name, it, item.spreadsOverMonth) }
        }
        return record.copy(
            revenue = record.revenue.copy(otherRevenue = cents(record.revenue.otherRevenue + moved(revenueItems))),
            costs = record.costs.copy(
                otherCosts = cents(record.costs.otherCosts + moved(costItems)),
                otherCostItems = itemsOn(costItems),
                otherRevenueItems = itemsOn(revenueItems)
            )
        )
    }

    /** [records] as their daily results count them ([resultDay]). */
    fun resultDays(records: Collection<DailyRecord>, ledger: List<FixedCost>): List<DailyRecord> =
        if (ledger.isEmpty()) records.toList() else records.map { resultDay(it, ledger) }

    private fun cents(value: Double): Double = kotlin.math.round(value * 100) / 100.0

    /** [result] ÷ [income] × 100; null without income. */
    fun percent(result: Double, income: Double): Double? = if (income > 0.0) result / income * 100.0 else null

    /** The planned side of [days]: all of them. */
    fun plan(days: Collection<DailyRecord>): PeriodResult =
        PeriodResult(days.sumOf { it.incomePlan }, days.sumOf { it.incomePlan - it.resultPlan }, days.size)

    /** The actual side of [days]: those with takings (the rest has no actual yet). */
    fun actual(days: Collection<DailyRecord>): PeriodResult {
        val traded = days.filter { it.revenue.realNetto != null }
        return PeriodResult(traded.sumOf { it.incomeReal }, traded.sumOf { it.incomeReal - it.resultReal }, traded.size)
    }
}

/** SH-195: a run of days' income, costs and result (sums of the days'). */
data class PeriodResult(val income: Double, val costs: Double, val days: Int) {
    val result: Double get() = income - costs
    val percent: Double? get() = DayResults.percent(result, income)
}
